import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { endPointMenu } from "modules/main/services/endpoints";
import { useFetch } from "modules/main/hooks/useFetch";
import MenuIcon, { AppMenuOptions } from "modules/main/types/menus";

const CATALOG_PATH_PREFIX = "/catalog";
const SKELETON_ROWS = 4;

/**
 * Route-gated category menu for the app sidebar.
 *
 * Renders the GET /api/sections payload (endPointMenu) as collapsible
 * category groups below the static "Catálogo" NavItem, visible only on
 * /catalog and /catalog/:section. The block is introduced by a separator
 * gated to catalog routes. Each category trigger shows its icon, name and a
 * rotating ChevronDown affordance (leaf categories get none) and toggles
 * its collapsible group without navigating; non-empty sub_menu entries
 * render as nested links to /catalog/{subMenu.slug}. The group containing
 * the active section auto-expands, the active sub-button is highlighted,
 * and every navigation closes the mobile drawer via setOpenMobile(false).
 * While the fetch is in flight a fixed-height skeleton placeholder occupies
 * the menu area; on fetch failure or empty payload the block renders
 * nothing. The catalog search input is NOT part of the sidebar — it lives
 * on the catalog pages (see modules/catalog/pages).
 *
 * @returns The sidebar catalog block, or null when the route is not a
 *   catalog route, the fetch failed, or no data is available.
 */
export const CategoriesMenu: React.FC = () => {
  const { pathname } = useLocation();
  const { setOpenMobile } = useSidebar();
  const { data, error, loading } = useFetch<AppMenuOptions[]>(endPointMenu);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  if (!pathname.startsWith(CATALOG_PATH_PREFIX)) {
    return null;
  }

  if (loading) {
    return (
      <>
        <Separator data-testid="catalog-menu-separator" className="my-4" />
        <SidebarMenu className="gap-3">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <SidebarMenuItem key={index}>
              <SidebarMenuSkeleton showIcon />
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </>
    );
  }

  if (error || !data || data.length === 0) {
    return null;
  }

  const currentSection = pathname.split("/")[2];

  const isGroupActive = (item: AppMenuOptions): boolean =>
    item.sub_menu?.some((subMenu) => subMenu.slug === currentSection) ?? false;

  return (
    <>
      <Separator data-testid="catalog-menu-separator" className="my-4" />
      <SidebarMenu className="gap-3">
        {data.map((item) => (
          <SidebarMenuItem key={item.id}>
            <Collapsible
              className="group/collapsible"
              open={openGroups[item.slug] ?? isGroupActive(item)}
              onOpenChange={(open) =>
                setOpenGroups((prev) => ({ ...prev, [item.slug]: open }))
              }
            >
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  isActive={item.slug === currentSection}
                  className="flex items-center gap-2"
                >
                  <MenuIcon iconName={item.icon} size={16} className={item.color} />
                  <span>{item.name}</span>
                  {item.sub_menu?.length ? (
                    <ChevronDown
                      data-testid="catalog-menu-chevron"
                      className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180"
                    />
                  ) : null}
                </SidebarMenuButton>
              </CollapsibleTrigger>
              {item.sub_menu?.length ? (
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.sub_menu.map((subMenu) => (
                      <SidebarMenuSubItem key={subMenu.id}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={subMenu.slug === currentSection}
                        >
                          <Link
                            to={`/catalog/${subMenu.slug}`}
                            onClick={() => setOpenMobile(false)}
                            className="flex items-center gap-2"
                          >
                            <MenuIcon iconName={subMenu.icon} size={16} className="" />
                            <span>{subMenu.name}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              ) : null}
            </Collapsible>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </>
  );
};